import { Box } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useNotify, useRefresh } from 'react-admin'
import { Wrap } from 'shared/components/Base/Base'
import { Button as CustomButton } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Option } from 'shared/components/Dropdown/Dropdown.styles'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { lightGray1 } from 'shared/theme/colors'
import styled from 'styled-components'

import { captureExceptionIfProduction } from 'helpers/utils'
import { checkOnboardingBackgroundCheck, orderOnboardingBackgroundCheck } from '../../api/core'
import { DEFAULT_ERROR_STRING } from '../../helpers/constants'

interface UserOnboardingCheckOrderProps {
  userId: string
  isInitial: boolean
  initialCheckShouldAlreadyExists?: boolean
  isCheckr: boolean
  disabled?: boolean
  onSuccess?: Handler
}

export const UserOnboardingCheckOrder = ({
  userId,
  isInitial,
  initialCheckShouldAlreadyExists,
  isCheckr,
  disabled,
  onSuccess,
}: UserOnboardingCheckOrderProps) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [status, setStatus] = useState<{ enabled: boolean; message: string } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    checkOnboardingBackgroundCheck(userId, isInitial).then(setStatus)
  }, [isInitial, userId])

  const isDisabled = status === null || disabled

  const onClick = () => {
    setIsLoading(true)
    orderOnboardingBackgroundCheck(userId, isInitial)
      .then(() => {
        setOpen(false)
        setIsLoading(false)
        notify(isCheckr ? `Checkr part ${isInitial ? '1' : '2'} ordered` : 'Success!', { type: 'success' })
        onSuccess?.()
        refresh()
      })
      .catch((error: any) => {
        setOpen(false)
        setIsLoading(false)
        console.error(error)
        notify(`${DEFAULT_ERROR_STRING}: ${JSON.stringify(error?.response?.data || error)}`, {
          type: 'error',
        })
        captureExceptionIfProduction(error)
      })
  }

  function renderDialog() {
    if (!status) return <InlineSpinner mt={4} mb={4} />

    if (status.enabled) {
      if (isInitial) {
        return (
          <>
            <Text variant="header2" align="left">
              {isCheckr
                ? 'Re-order Checkr part 1 background checks'
                : 'Re-order initial report on HireRight?'}
            </Text>

            {isCheckr && (
              <Text variant="paragraph1" mt={1} align="left">
                <ul style={{ paddingLeft: '15px' }}>
                  <LI>MVR</LI>
                  <LI>CDLIS</LI>
                  <LI>DAC</LI>
                </ul>
              </Text>
            )}

            {isCheckr && (
              <Text variant="paragraph1" mt={1} mb={1} align="left">
                Please keep in mind that placing this order will incur costs to CloudTrucks.
              </Text>
            )}

            <Box mt={6} />
            <CustomButton margin="0 auto" variant="primary" onClick={onClick} isLoading={isLoading}>
              Place Order
            </CustomButton>
            <Box mt={2} />
            <CustomButton margin="0 auto" variant="text" onClick={() => setOpen(false)}>
              Cancel
            </CustomButton>
          </>
        )
      } else {
        return (
          <>
            <Text variant="header2" align="left">
              {isCheckr ? 'Order Checkr part 2 background checks' : 'Order additional reports on HireRight?'}
            </Text>
            {!isCheckr && (
              <Text variant="paragraph1" mt={1} mb={1} align="left">
                Following reports will be ordered. Additionally, the driver will receive an e-mail to provide
                consent and schedule drug testing.
              </Text>
            )}

            <Text variant="paragraph1" mt={1} align="left">
              <ul style={{ paddingLeft: '15px' }}>
                {isCheckr ? (
                  <>
                    <LI>County criminal search (Last 7 years)</LI>
                    <LI>Drug Screening (DS DOT 7 PANEL</LI>
                    <LI>National Criminal Search (Standard)</LI>
                    <LI>SSN Trace</LI>
                    <LI>Global Watchlist Search</LI>
                    <LI>Drug Alcohol Clearninghouse</LI>
                  </>
                ) : (
                  <>
                    <LI>Criminal Felony & Misdemeanor</LI>
                    <LI>SSN Trace</LI>
                    <LI>Widescreen Plus</LI>
                    <LI>National Criminal Search </LI>
                    <LI>DOT Compliance (FMCSA) </LI>
                    <LI>DOT Drug/Alcohol Verification (FMCSA) </LI>
                    <LI>DOT Drug Testing</LI>
                  </>
                )}
              </ul>
            </Text>

            {isCheckr && (
              <Text variant="paragraph1" mt={1} mb={1} align="left">
                Please keep in mind that placing this order will incur costs to CloudTrucks.
              </Text>
            )}

            <Box mt={6} />
            <CustomButton margin="0 auto" variant="primary" onClick={onClick} isLoading={isLoading}>
              Place Order
            </CustomButton>
            <Box mt={2} />
            <CustomButton margin="0 auto" variant="text" onClick={() => setOpen(false)}>
              Cancel
            </CustomButton>
          </>
        )
      }
    }

    return (
      <>
        <Text variant="header2" align="left">
          Order {isCheckr ? 'Checkr' : 'HireRight'} {isInitial ? 'part 1' : 'part 2'} background checks
        </Text>
        <Text variant="paragraph1" mt={1} mb={4} align="left">
          {status.message}
        </Text>

        <Box mt={4} />
        <CustomButton margin="0 auto" variant="text" onClick={() => setOpen(false)}>
          Cancel
        </CustomButton>
      </>
    )
  }

  return (
    <div>
      <StyledOption onClick={() => (isDisabled ? null : setOpen(true))} $isDisabled={isDisabled}>
        {isInitial
          ? `${initialCheckShouldAlreadyExists ? 'Reorder' : 'Order'} package 1 (initial reports)`
          : 'Order package 2 (additional reports)'}
      </StyledOption>
      <SimpleDialog isOpen={open} onClose={() => setOpen(false)} maxWidth="572px">
        <Wrap width="700px" maxWidth="100%">
          {renderDialog()}
        </Wrap>
      </SimpleDialog>
    </div>
  )
}

const LI = styled.li`
  padding-top: 5px;
`

const StyledOption = styled(Option)<{ $isDisabled?: boolean }>`
  ${({ $isDisabled }) => $isDisabled && `color: ${lightGray1}`}
`
